import React, { useState, useEffect } from 'react';
import { Sparkles, Package, Bot, PenTool, Lock, Bookmark, Image, Eye, User, Loader2, Search, SlidersHorizontal, Plus } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import MyDesignsPage from './MyDesignsPage';
import BookmarkButton from '../components/BookmarkButton';
import ScrollableTabs from '../components/ScrollableTabs';
import FloatingActionButton from '../components/FloatingActionButton';
import CreateDesignModal from '../components/CreateDesignModal';
import { getAuthHeaders } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

const CreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read tab from URL params, then navigation state, then default
  const paramTab = searchParams.get('tab');
  const stateTab = location.state?.activeTab;
  const initialTab = paramTab || stateTab || 'launchpads';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [designsRefreshKey, setDesignsRefreshKey] = useState(0);

  // Sync activeTab with URL params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const launchpads = [
    {
      id: 'spark',
      title: 'Spark',
      description: 'Get inspired when you\'re feeling crafty but don\'t have ideas yet',
      icon: Sparkles,
      color: 'from-amber-400 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      comingSoon: true,
      features: ['Trending techniques', 'Seasonal occasions', 'New from your stash']
    },
    {
      id: 'product',
      title: 'Product',
      description: 'Start with a specific stash item and discover what you can make',
      icon: Package,
      color: 'from-rose-400 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      comingSoon: true,
      features: ['Select from stash', 'Technique suggestions', 'Project ideas']
    },
    {
      id: 'copilot',
      title: 'Co-Pilot',
      description: 'You know what you want to make — let\'s design it together',
      icon: Bot,
      color: 'from-sage to-emerald-500',
      bgColor: 'bg-sage-light',
      borderColor: 'border-sage',
      comingSoon: true,
      features: ['Guided creation', 'Technique tips', 'Product recommendations']
    }
  ];

  // Fetch bookmarks when tab is active
  useEffect(() => {
    if (activeTab === 'bookmarks') {
      fetchBookmarks();
    }
  }, [activeTab]);

  const fetchBookmarks = async () => {
    setBookmarksLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookmarks?limit=50`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setBookmarks(data.designs || []);
        setBookmarkedIds(new Set((data.designs || []).map(d => d.design_id)));
      }
    } catch (err) {
      console.error('Failed to fetch bookmarks:', err);
    } finally {
      setBookmarksLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    // Update URL params (like Explore does) - this prevents scroll reset
    setSearchParams({ tab }, { replace: true });
    setActiveTab(tab);
    // Clear search when switching tabs
    setSearchQuery('');
    setShowFilters(false);
  };

  return (
    <div className="min-h-screen bg-cream pb-20 lg:pb-0">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-3 pb-6 sm:py-6">
        {/* Header - Compact */}
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <h1 className="text-lg sm:text-2xl font-light text-charcoal">Create</h1>
          </div>
        </div>

        {/* Search Bar (non-functional for now) with Filter Toggle */}
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-light" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all bg-white text-sm"
              data-testid="create-search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-lg border transition-all ${
              showFilters
                ? 'border-sage bg-sage/10 text-sage'
                : 'border-beige bg-white text-gray-soft hover:text-charcoal'
            }`}
            title="Filters"
            data-testid="create-filter-toggle-btn"
          >
            <SlidersHorizontal size={18} />
          </button>
        </div>

        {/* Collapsible Filter Panel - placeholder for future */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-4 p-2 sm:p-3 bg-white rounded-lg border border-beige">
            <p className="text-sm text-gray-soft">Filters coming soon...</p>
          </div>
        )}

        {/* Tab Navigation - always rendered to preserve scroll position */}
        <ScrollableTabs className="mb-2 sm:mb-4 border-b border-beige">
          <button
            onClick={() => handleTabChange('launchpads')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'launchpads'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
          >
            <span className="flex items-center gap-2">
              <Sparkles size={16} />
              Launchpads
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Soon</span>
            </span>
            {activeTab === 'launchpads' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
          <button
            onClick={() => handleTabChange('designs')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'designs'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
          >
            <span className="flex items-center gap-2">
              <PenTool size={16} />
              My Designs
            </span>
            {activeTab === 'designs' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
          <button
            onClick={() => handleTabChange('bookmarks')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap shrink-0 ${
              activeTab === 'bookmarks'
                ? 'text-sage'
                : 'text-gray-soft hover:text-charcoal'
            }`}
          >
            <span className="flex items-center gap-2">
              <Bookmark size={16} />
              Bookmarks
            </span>
            {activeTab === 'bookmarks' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sage" />
            )}
          </button>
        </ScrollableTabs>

        {/* Tab Content */}
        {activeTab === 'designs' ? (
          /* My Designs - embedded mode */
          <MyDesignsPage embedded={true} refreshKey={designsRefreshKey} />
        ) : activeTab === 'bookmarks' ? (
          /* Bookmarks Grid */
          <div>
            {bookmarksLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-sage" size={32} />
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-beige">
                <Bookmark size={48} className="mx-auto text-beige mb-4" />
                <h3 className="text-lg font-medium text-charcoal mb-2">No bookmarks yet</h3>
                <p className="text-sm text-gray-soft mb-6 max-w-md mx-auto">
                  Save designs for inspiration by clicking the bookmark icon when exploring community designs.
                </p>
                <button
                  onClick={() => navigate('/explore')}
                  className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm"
                >
                  Explore Designs
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {bookmarks.map((design) => (
                  <div
                    key={design.design_id}
                    className="bg-white rounded-xl border border-beige overflow-hidden group hover:shadow-md transition-shadow"
                  >
                    <div
                      className="aspect-square bg-cream relative cursor-pointer"
                      onClick={() => navigate(`/designs/${design.design_id}`, { state: { from: 'create', tab: 'bookmarks' } })}
                    >
                      {design.image_url ? (
                        <img src={design.image_url} alt={design.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image size={48} className="text-beige" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <BookmarkButton 
                          designId={design.design_id} 
                          initialBookmarked={true}
                          size="small"
                          onToggle={(isBookmarked) => {
                            if (!isBookmarked) {
                              setBookmarks(prev => prev.filter(d => d.design_id !== design.design_id));
                            }
                          }}
                        />
                      </div>
                      {design.view_count > 0 && (
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-charcoal/40 text-white rounded-full text-xs flex items-center gap-1">
                          <Eye size={10} />
                          {design.view_count}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-charcoal truncate">{design.title}</h3>
                      {design.creator && (
                        <div className="flex items-center gap-2 mt-2">
                          {design.creator.picture ? (
                            <img src={design.creator.picture} alt="" className="w-5 h-5 rounded-full" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-sage-light flex items-center justify-center">
                              <User size={10} className="text-sage" />
                            </div>
                          )}
                          <span className="text-xs text-gray-soft truncate">
                            {design.creator.handle ? `@${design.creator.handle}` : design.creator.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Launchpads Grid */
          <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {launchpads.map((pad) => {
              const Icon = pad.icon;
              return (
                <div
                  key={pad.id}
                  className={`relative rounded-xl border-2 ${pad.borderColor} ${pad.bgColor} p-6 transition-all duration-200 opacity-75`}
                >
                  {/* Coming Soon Badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 text-xs font-medium text-gray-soft bg-white/80 px-2 py-1 rounded-full">
                    <Lock size={12} />
                    Coming Soon
                  </div>

                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pad.color} flex items-center justify-center mb-4`}>
                    <Icon size={24} className="text-white" />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-lg font-medium text-charcoal mb-2">{pad.title}</h3>
                  <p className="text-sm text-gray-soft mb-4">{pad.description}</p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {pad.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-gray-soft">
                        <div className="w-1 h-1 rounded-full bg-sage" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Info Box */}
          <div className="bg-white rounded-xl border border-beige p-6">
            <h3 className="font-medium text-charcoal mb-2">What are Launchpads?</h3>
            <p className="text-sm text-gray-soft">
              Launchpads are your creative starting points. Whether you're looking for inspiration, 
              want to use specific products from your stash, or have a clear vision to bring to life — 
              there's a launchpad for every creative mood.
            </p>
          </div>
        </div>
        )}
      </div>

      {/* FAB for My Designs tab */}
      {activeTab === 'designs' && (
        <div className="sm:hidden">
          <FloatingActionButton
            onClick={() => setShowCreateModal(true)}
            label="New Design"
            testId="new-design-fab"
          />
        </div>
      )}

      {/* Desktop Add Button for My Designs tab */}
      {activeTab === 'designs' && (
        <div className="hidden sm:block fixed bottom-8 right-8 z-40">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-3 bg-sage text-white rounded-full shadow-lg hover:bg-sage-dark transition-colors font-medium"
            data-testid="add-design-btn-desktop"
          >
            <Plus size={20} />
            New Design
          </button>
        </div>
      )}

      {/* Create Design Modal */}
      {showCreateModal && (
        <CreateDesignModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            setDesignsRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
};

export default CreatePage;
